import { IcebergIcon } from '@tradesolution/iceberg-ui-react';
import BrregPopover from 'components/BrregPopover';
import { useEffect, useRef, useState } from 'react';
import { Button, Form, InputGroup, OverlayTrigger } from 'react-bootstrap';
import { AsyncTypeahead, Highlighter } from 'react-bootstrap-typeahead';
import BrregApi from 'services/KjederegisteretAdminApi/BrregApi';
import { BrregEnhetResponse } from 'services/KjederegisteretAdminApi/BrregApi/types';
import EnhetApi from 'services/KjederegisteretAdminApi/EnhetApi';
import { EnhetSearchFilter } from 'services/KjederegisteretAdminApi/EnhetSearchApi/types';
import querystring from 'query-string';

export interface BrRegInfo {
  name: string;
  orgNr: string;
  isBedriftsNr?: boolean;
}

interface Props {
  type: 'bedriftsnr' | 'orgnr';
  selected: BrRegInfo | undefined;
  onChange: (value: BrRegInfo | undefined) => void;
  size?: 'sm' | 'lg';
  onJuridiskNameMismatch?: (mismatch: boolean) => void;
  // If true, do not fetch or display the connected enhet count
  disableConnectedEnhetCount?: boolean;
  // Daglig leder is already fetched here for the BrregPopover - this lets a caller reuse that
  // same fetch instead of hitting Brreg again for the same orgnr.
  onDagligLederChanged?: (navn: string | undefined) => void;
  // Same idea for naeringskode1 - already part of the BrregEnhetResponse this component fetches.
  onNaeringskodeChanged?: (naeringskode: { kode: string; beskrivelse: string } | undefined) => void;
}

const OrgnrInput = (props: Props) => {
  const [selectedBrregEnhetResponse, setSelectedBrregEnhetResponse] = useState<
    BrregEnhetResponse | undefined
  >(undefined);
  const [dagligLeder, setDagligLeder] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [isAdopting, setIsAdopting] = useState(false);
  const [searchResult, setSearchResult] = useState<BrregEnhetResponse[]>([]);
  const [isInvalid, setIsInvalid] = useState<boolean>(false);
  const [enhetCount, setEnhetCount] = useState<number | undefined>(undefined);
  const [currentOrgnr, setCurrentOrgnr] = useState<string | undefined>(undefined);

  const typeaheadRef = useRef(null);
  const lastOrgQueryIdRef = useRef(0);
  const lastCountQueryIdRef = useRef(0);

  useEffect(() => {
    setIsInvalid(false);
    setIsLoading(true);
    setEnhetCount(undefined);

    if (props.selected?.orgNr) {
      BrregApi.getByOrgNr(props.selected.orgNr).then((data) => {
        if (data && data.length > 0) {
          const first = data[0];
          setSelectedBrregEnhetResponse(first);
          setIsInvalid(false);
          props.onNaeringskodeChanged?.(first.naeringskode1);
        }
      });

      // Also fetch how many enheter are connected to this orgnr
      if (!props.disableConnectedEnhetCount) {
        const trimmed = props.selected.orgNr.trim();
        setCurrentOrgnr(trimmed);
        if (/^\d{9}$/.test(trimmed)) {
          EnhetApi.getCountByOrgnr(trimmed)
            .then((count) => setEnhetCount(count))
            // eslint-disable-next-line no-console
            .catch((err) => console.error('Feil ved henting av antall enheter for orgnr', err));
        } else {
          setEnhetCount(undefined);
        }
      }
    } else {
      setSelectedBrregEnhetResponse(undefined);
      setEnhetCount(undefined);
      setCurrentOrgnr(undefined);
      props.onNaeringskodeChanged?.(undefined);
    }

    setIsLoading(false);
  }, [props.selected?.orgNr]);

  const normalize = (s?: string | null) =>
    (s ?? '').trim().toUpperCase().replace(/\s+/g, ' ').replace(/[`´]/g, "'");
  const toCanonicalName = normalize;
  const nameDiffersFromBrreg =
    !!props.selected?.name &&
    !!selectedBrregEnhetResponse?.name &&
    !isInvalid &&
    normalize(props.selected.name) !== normalize(selectedBrregEnhetResponse.name);

  useEffect(() => {
    if (props.onJuridiskNameMismatch && nameDiffersFromBrreg) {
      props.onJuridiskNameMismatch(nameDiffersFromBrreg);
    }
  }, [nameDiffersFromBrreg, props]);

  const handleAdoptBrregName = async () => {
    if (!props.selected?.orgNr) return;
    try {
      setIsAdopting(true);
      const result = await BrregApi.getByOrgNr(props.selected.orgNr);
      if (Array.isArray(result) && result.length > 0) {
        const fresh = result[0];
        const canonical = toCanonicalName(fresh.name);
        const updated: BrregEnhetResponse = { ...fresh, name: canonical };
        // Use common selection logic to propagate upward (will also set state)
        handleEnhetSelected(updated);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Adopt refetch failed', err);
    } finally {
      setIsAdopting(false);
    }
  };

  useEffect(() => {
    if (props.selected?.orgNr) {
      BrregApi.getDagligLeder(props.selected?.orgNr).then((dagligLeder) => {
        setDagligLeder(dagligLeder);
        props.onDagligLederChanged?.(dagligLeder);
      });
    }
  }, [props.selected?.orgNr]);

  const renderMenuItem = (option: BrregEnhetResponse, props: any, index: number) => {
    const itemDesc = `${option.name} ${option.orgNr}`;
    return (
      <div key={index}>
        <Highlighter search={props.text}>{itemDesc}</Highlighter>
      </div>
    );
  };

  const handleEnhetSelected = (e?: BrregEnhetResponse) => {
    if (!e) {
      setSelectedBrregEnhetResponse(undefined);
      props.onChange(undefined);
      return;
    }
    const canonical = toCanonicalName(e.name);
    const normalized: BrregEnhetResponse = { ...e, name: canonical };
    setSelectedBrregEnhetResponse(normalized);
    props.onChange({ name: canonical, orgNr: e.orgNr, isBedriftsNr: !!e.overordnetEnhet });
    // Validation logic depending on expected type
    if (props.type === 'orgnr') {
      // Expect juridisk (no overordnetEnhet)
      setIsInvalid(!!e.overordnetEnhet);
    } else if (props.type === 'bedriftsnr') {
      // Expect bedriftsnummer (has overordnetEnhet)
      setIsInvalid(!e.overordnetEnhet);
    } else {
      setIsInvalid(false);
    }
  };

  const cleanInput = (input: string) => {
    return input.replaceAll(' ', '').replaceAll('.', '');
  };

  const lookupOrgnr = async (orgnr: string) => {
    const queryId = ++lastOrgQueryIdRef.current;
    try {
      setIsLoading(true);
      // Start count lookup in parallel when it looks like a valid orgnr (9 digits)
      const trimmed = cleanInput(orgnr ?? '');
      setEnhetCount(undefined);
      setCurrentOrgnr(trimmed);
      const countQueryId = ++lastCountQueryIdRef.current;
      if (!props.disableConnectedEnhetCount && /^\d{9}$/.test(trimmed)) {
        EnhetApi.getCountByOrgnr(trimmed)
          .then((count) => {
            if (lastCountQueryIdRef.current === countQueryId) setEnhetCount(count);
          })
          // eslint-disable-next-line no-console
          .catch((err) => {
            if (lastCountQueryIdRef.current === countQueryId) setEnhetCount(undefined);
            console.error('Feil ved henting av antall enheter for orgnr', err);
          });
      }
      const result = await BrregApi.getByOrgNr(trimmed);
      if (lastOrgQueryIdRef.current !== queryId) return; // stale
      if (Array.isArray(result) && result.length > 0) {
        setSearchResult(result);
        setIsInvalid(false);
      } else {
        setIsInvalid(true);
        setSelectedBrregEnhetResponse(undefined);
        setSearchResult([]);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('OrgnrInput orgnr lookup error', err);
      setSelectedBrregEnhetResponse(undefined);
      setSearchResult([]);
    } finally {
      if (lastOrgQueryIdRef.current === queryId) {
        setIsLoading(false);
      }
    }
  };

  // If the disabling prop toggles on, ensure we clear any previously shown count
  useEffect(() => {
    if (props.disableConnectedEnhetCount) {
      setEnhetCount(undefined);
    }
  }, [props.disableConnectedEnhetCount]);

  return (
    <div>
      <InputGroup size={props.size ? props.size : undefined}>
        {!selectedBrregEnhetResponse ? (
          <InputGroup.Text style={{ paddingLeft: '.5rem', paddingRight: '.5rem' }}>
            <IcebergIcon icon="search" />
          </InputGroup.Text>
        ) : (
          <OverlayTrigger
            rootClose
            trigger="click"
            placement="bottom"
            overlay={<BrregPopover data={selectedBrregEnhetResponse} dagligLeder={dagligLeder} />}
          >
            {selectedBrregEnhetResponse.slettedato || selectedBrregEnhetResponse.finnesIkke ? (
              <Button
                variant="warning"
                id="button-addon1"
                style={{ paddingLeft: '.4rem', paddingRight: '.4rem' }}
              >
                <IcebergIcon cursor="pointer" icon="check" bold />
              </Button>
            ) : (
              <Button
                variant="success"
                id="button-addon1"
                style={{ paddingLeft: '.4rem', paddingRight: '.4rem' }}
              >
                <IcebergIcon cursor="pointer" icon="check" bold />
              </Button>
            )}
          </OverlayTrigger>
        )}
        <AsyncTypeahead
          id="brregTypeAhead"
          filterBy={() => true}
          labelKey={(option: BrregEnhetResponse) =>
            `${option.juridisknavn ?? option.name} ${option.orgNr}`
          }
          renderMenuItemChildren={(option: BrregEnhetResponse, props: any, index: number) =>
            renderMenuItem(option, props, index)
          }
          useCache={false}
          minLength={3}
          clearButton
          isLoading={isLoading}
          options={searchResult ?? []}
          searchText="Søker"
          placeholder="Søk i brønnøysundregistrene..."
          promptText="Skriv for å søke"
          emptyLabel="Fant ingen treff"
          maxResults={10}
          onChange={(e: BrregEnhetResponse[]) => handleEnhetSelected(e[0])}
          onSearch={(q) => lookupOrgnr(q)}
          isInvalid={(!isLoading && isInvalid) || undefined}
          ref={typeaheadRef}
          selected={
            selectedBrregEnhetResponse
              ? [
                  {
                    name: props.selected?.name ?? selectedBrregEnhetResponse.name,
                    orgNr: selectedBrregEnhetResponse.orgNr,
                  },
                ]
              : []
          }
        />
      </InputGroup>
      {nameDiffersFromBrreg && (
        <Form.Text as="span" className="d-block mt-1">
          <Button
            variant="link"
            className="p-0 text-warning text-decoration-underline text-start fw-semibold align-baseline"
            style={{ cursor: 'pointer' }}
            onClick={handleAdoptBrregName}
            disabled={isAdopting}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleAdoptBrregName();
              }
            }}
            aria-label={
              selectedBrregEnhetResponse?.name
                ? `Oppdater juridisk navn til ${selectedBrregEnhetResponse.name}`
                : 'Oppdater juridisk navn fra Brønnøysund'
            }
            title={
              selectedBrregEnhetResponse?.name
                ? `Klikk for å bruke navnet «${selectedBrregEnhetResponse.name}»`
                : 'Klikk for å bruke navnet fra Brønnøysund'
            }
            data-testid="adopt-brreg-name-button"
          >
            {isAdopting ? (
              <>Oppdaterer…</>
            ) : (
              <>
                Brønnøysund har et annet navn registrert. Klikk for å oppdatere til
                {selectedBrregEnhetResponse?.name ? `: ${selectedBrregEnhetResponse.name}` : ''}
              </>
            )}
          </Button>
        </Form.Text>
      )}
      {props.type === 'orgnr' && selectedBrregEnhetResponse?.overordnetEnhet && (
        <Form.Text className="text-danger d-block mt-1" data-testid="bedriftsnr-invalid-msg">
          Dette er et bedriftsnummer. Velg juridisk enhet (organisasjonsnummer) i stedet.
        </Form.Text>
      )}
      {props.type === 'bedriftsnr' &&
        selectedBrregEnhetResponse &&
        !selectedBrregEnhetResponse.overordnetEnhet && (
          <Form.Text className="text-danger d-block mt-1" data-testid="juridisk-invalid-msg">
            Dette er et organisasjonsnummer. Velg et bedriftsnummer (underliggende enhet) i stedet.
          </Form.Text>
        )}
      {!props.disableConnectedEnhetCount && typeof enhetCount === 'number' && (
        <Form.Text className="d-block mt-1" data-testid="enhet-count-msg">
          {(() => {
            const buildSearchUrl = (criteria: string) => {
              const filter = new EnhetSearchFilter();
              filter.criteria = criteria;
              const searchstring = querystring.stringify(filter, {
                arrayFormat: 'bracket',
                skipNull: true,
              });
              return `/enheter?${searchstring}`;
            };
            const criteria = currentOrgnr ?? props.selected?.orgNr ?? '';
            const url = criteria ? buildSearchUrl(criteria) : undefined;
            return (
              <>
                {url ? (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Åpne enhetssøk for orgnr ${criteria}`}
                  >
                    {enhetCount > 1 ? `${enhetCount - 1} andre enheter` : 'Ingen andre enheter'} med
                    dette organisasjonsnummeret
                  </a>
                ) : (
                  <>
                    {enhetCount > 1 ? `${enhetCount - 1} andre enheter` : 'Ingen andre enheter'} med
                    dette organisasjonsnummeret
                  </>
                )}
              </>
            );
          })()}
        </Form.Text>
      )}
    </div>
  );
};

export default OrgnrInput;
